import { Component, OnInit, OnDestroy, ViewContainerRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material';
import { FacebookService, InitParams, UIParams } from 'ngx-facebook';
import { TdLoadingService, TdDialogService } from '@covalent/core';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';

import { Idea } from '../../shared';
import { IdeaService } from '../../services';
import { IdeaSignDialogComponent } from '../idea-sign-dialog/idea-sign-dialog.component';
import { ParticipantListDialogComponent } from '../participant-list-dialog/participant-list-dialog.component';
import { DeadlineDialogComponent } from '../deadline-dialog/deadline-dialog.component';
import { DecisionDialogComponent } from '../decision-dialog/decision-dialog.component';
import { PetitionAdminConfigs } from '../../shared/admin-models/interfaces/petition-admin-configs.interface';
import { AdminService } from '../../services/admin/admin.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-idea-details',
  templateUrl: './idea-details.component.html',
  styleUrls: ['./idea-details.component.scss']
})
export class IdeaDetailsComponent implements OnInit, OnDestroy {
  /**
   * The idea object
   */
  idea: Idea;

  /**
   * Session data
   */
  sessionData: { [key: string]: any };

  /**
   * Subscriptions
   */
  private _subs: Set<Subscription> = new Set();

  /**
   * Error keyword/message map
   */
  static readonly errorMap: {
    [errorKey: string]: string;
  } = {
    SIGNER_DATA_NOT_VALID: 'პირადი მონაცემები არასწორად არის მითითებული.',
    SIGN_IS_NOT_PERMITTED: 'თქვენ არ გაქვთ ხმის მიცემის უფლება.',
    PID_ALREADY_IN_USE_FOR_SIGN: 'თქვენი ხმა უკვე დაფიქსირებულია.',
    SIGNER_AGE_LIMIT:
      'თქვენ არ გაქვთ ხმის მიცემის უფლება, რადგან არ ხართ სრულწლოვანი',
    NOT_REGISTERED_IN_TBILISI:
      'თქვენ არ გაქვთ ხმის მიცემის უფლება, რადგან არ ხართ რეგისტრირებული ქალაქ თბილისში.'
  };
  signersNeededAmount: number;
  regionValidationServiceEnabled: boolean;

  /**
   * Constructor
   *
   * @param _router
   * @param _activatedRoute
   * @param _viewContainerRef
   * @param _matDialog
   * @param _facebookService
   * @param _loadingService
   * @param _dialogService
   * @param _ideaService
   */
  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _viewContainerRef: ViewContainerRef,
    private _matDialog: MatDialog,
    private _facebookService: FacebookService,
    private _loadingService: TdLoadingService,
    private _dialogService: TdDialogService,
    private _ideaService: IdeaService,
    private adminService: AdminService
  ) {
    const initParams: InitParams = {
      appId: '577951119212572',
      xfbml: true,
      version: 'v2.8'
    };

    _facebookService.init(initParams);
  }

  /**
   * Init
   */
  ngOnInit() {
    this._fetchIdeaDetails();
    this.getconfigs();
  }

  private _fetchIdeaDetails() {
    this._subs.add(
      Observable.combineLatest(
        [this._activatedRoute.data, this._activatedRoute.params],
        (data, params) => ({ data, params })
      ).subscribe((ap) => {
        const { id } = ap.params;

        this.sessionData = ap.data.sessionData;

        this._loadingService.register();
        this._ideaService
          .searchFull({ id })
          .finally(() => {
            this._loadingService.resolve();
          })
          .subscribe(
            (res: any) => {
              if (res['errors']) {
                // console.log(res);
              } else if (res['result'].data) {
                if (res['result'].data.length > 0) {
                  this.idea = Object.assign(
                    { metaData: {} },
                    res['result'].data[0]
                  );
                } else {
                  this._router.navigate(['/idea-list']);
                }
              }
            },
            (err) => {
              this._router.navigate(['/home']);
            }
          );
      })
    );
  }
  getconfigs(): void {
    this.adminService.getConfigs().subscribe((res: PetitionAdminConfigs) => {
      this.signersNeededAmount = res.petitionCounterValue;
      this.regionValidationServiceEnabled = res.regionValidationServiceEnabled;
    });
  }
  /**
   * Destroy
   */
  ngOnDestroy() {
    this._subs.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Status text map
   */
  get statusText() {
    return IdeaService.statusText;
  }

  /**
   * Status color map
   */
  get statusColor() {
    return IdeaService.statusColor;
  }

  /**
   * Normalized status text map
   */
  get statusTextNormalized() {
    const statusText = {};
    Object.keys(this.statusText).forEach(
      (key) => (statusText[key] = this.statusText[key].split(' / ')[0])
    );
    return statusText;
  }

  /**
   * Property to check idea owner
   */
  get isMine() {
    return this.idea && this.idea.authorId === this.sessionData.id;
  }

  /**
   * Property to check sign
   */
  get isSignDisabled() {
    return this.idea && this.idea.statusId !== 3;
  }

  /**
   * Property to check defected status
   */
  get isDefected() {
    return this.idea && this.idea.statusId === 11;
  }

  /**
   * Idea image src
   */
  get imageSrc() {
    return new URL(`/idea/file/${this.idea.imageFileId}`, environment.apiUlr).toString();
  }

  /**
   * Optional file src
   */
  get optionalFileSrc() {
    return new URL(`/idea/file/${this.idea.optionalFileId}`, environment.apiUlr).toString();
  }

  /**
   * Economical ground file src
   */
  get economicalGroundFileSrc() {
    return new URL(`/idea/file/${this.idea.economicalGroundFileId}`, environment.apiUlr).toString();
  }

  /**
   * Opens alert dialog
   *
   * @param message
   * @param title
   */
  private _openAlert(message: string, title: string = '') {
    return this._dialogService.openAlert({
      message: message,
      viewContainerRef: this._viewContainerRef, //OPTIONAL
      title: title, //OPTIONAL, hides if not provided
      closeButton: 'დახურვა', //OPTIONAL, defaults to 'CLOSE'
      width: '400px' //OPTIONAL, defaults to 400px
    });
  }

  /**
   * Sign idea
   *
   * @param form
   */
  private _sign(form: FormGroup) {
    if (form && form.valid) {
      const data = Object.assign({ petitionId: this.idea.id }, form.value);
      this._loadingService.register();
      this._ideaService
        .sign(data)
        .finally(() => {
          this._loadingService.resolve();
        })
        .subscribe((res: any) => {
          if (res['errors']) {
            const errorKey = res.result['errors'][0].keyword.replace(
                /___ERROR_NOT_FOUND$/,
                ''
              ),
              errorMessage =
                IdeaDetailsComponent.errorMap[errorKey] || errorKey;
            this._openAlert(errorMessage);
          } else if (res['result'].data) {
            this._openAlert('გმადლობთ მონაწილეობისთვის')
              .afterClosed()
              .subscribe(() => {
                // Update data on the page
                this._fetchIdeaDetails();
                this.getconfigs();
              });
          }
        });
    }
  }

  /**
   * Facebook sharing
   */
  share(e: Event) {
    const params: UIParams = {
      method: 'share_open_graph',
      action_type: 'og.shares',
      action_properties: JSON.stringify({
        object: {
          'og:url': location.href,
          'og:title': this.idea.title,
          'og:description': this.idea.description,
          'og:image': this.imageSrc,
          'og:image:secure_url': this.imageSrc
        }
      })
    };

    this._facebookService.ui(params).then(
      () => {},
      (err) => {}
    );
  }

  /**
   * Opens sign dialog
   *
   * @param event
   */
  openSignDialog(event: Event) {
    const data = {
      regionValidationServiceEnabled: this.regionValidationServiceEnabled
    };

    this._matDialog
      .open(IdeaSignDialogComponent, { data, width: '60%' })
      .afterClosed()
      .subscribe((form: FormGroup) => this._sign(form));
  }

  /**
   * Opens participant list dialog
   * @param event
   */
  openParticipantListDialog(event: Event) {
    if (this.idea.signerQuantity > 0) {
      this._matDialog.open(ParticipantListDialogComponent, {
        width: '60%',
        data: this.idea
      });
    }
  }

  /**
   * Opens deadline dialog
   * @param event
   */
  openDeadlineDialog(event: Event) {
    const { id } = this.idea;
    this._loadingService.register();
    this._ideaService
      .getDeadlines({ petitionId: id })
      .finally(() => {
        this._loadingService.resolve();
      })
      .subscribe((res: any) => {
        if (res['errors']) {
          // console.log(res['errors']);
        } else if (res['result'].data && res['result'].data.length > 0) {
          this._matDialog.open(DeadlineDialogComponent, {
            width: '70%',
            data: res['result'].data
          });
        }
      });
  }

  /**
   * Opens decision dialog
   * @param event
   */
  openDecisionDialog(event: Event) {
    const { id } = this.idea;
    this._loadingService.register();
    this._ideaService
      .decisionSearch({ petitionId: id })
      .finally(() => {
        this._loadingService.resolve();
      })
      .subscribe((res: any) => {
        if (res['errors']) {
          // console.log(res['errors']);
        } else if (res['result'].data && res['result'].data.length > 0) {
          res['result'].data.map((decision) =>
            Object.assign(decision, { petitionStatusId: this.idea.statusId })
          );
          this._matDialog.open(DecisionDialogComponent, {
            width: '40%',
            data: res['result'].data
          });
        }
      });
  }
}



// WEBPACK FOOTER //
// /opt/app-root/src/src/app/components/idea-details/idea-details.component.ts